import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';
import { debounce } from 'lodash';
import { useCurrentTheme } from '../../hooks/useCurrentTheme';
import Widget from '..';
import { useSelector } from 'react-redux';
import { examsSelectors } from '../../slices/exams/examsSlice';
import { fetchAllExamSessions } from '../../services/studySession';
import { StudySession } from '../../types/Study';
import { Select, SelectChangeEvent, MenuItem } from '@mui/material';
import { FlashcardTrendDataPoint, WIDGET_KEYS } from '../../types/Widgets';
import { FLASHCARD_TREND_FAKE_DATA } from '../../utils/fakeData';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface FlashcardTrendProps {
  isDemo?: boolean;
  showDelete?: boolean;
}

const FlashcardTrend: React.FC<FlashcardTrendProps> = ({
  isDemo = false,
  showDelete = false,
}) => {
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const [flashcardTrendData, setFlashcardTrendData] = useState<
    FlashcardTrendDataPoint[]
  >([]);

  const chartRef = useRef<ChartJS<'line', number[], string> | null>(null);
  const { isDarkMode } = useCurrentTheme();

  // State for selected month/year
  const today = moment();
  const [selectedMonth, setSelectedMonth] = useState<number>(today.month()); // 0-indexed
  const [selectedYear, setSelectedYear] = useState<number>(today.year());

  useEffect(() => {
    if (primaryExam && !isDemo) {
      fetchAllPastStudySessions();
    }
    if (isDemo) {
      setFlashcardTrendData(FLASHCARD_TREND_FAKE_DATA);
    }
  }, [primaryExam, isDemo]);

  const fetchAllPastStudySessions = async () => {
    if (primaryExam) {
      fetchAllExamSessions(primaryExam.id).then((response) => {
        if (response) {
          const sessions = [...response];
          const data = sessions.map((session: StudySession) => {
            return {
              date: session.date,
              completedFlashcardCount: session.completedFlashcardCount,
            };
          });
          setFlashcardTrendData(data);
        }
      });
    }
  };

  // Get all months/years present in the data
  const allMonths = Array.from(
    new Set(
      (flashcardTrendData || []).map((dp) => moment(dp.date).format('YYYY-MM'))
    )
  ).sort();

  // Filter data for selected month/year
  const filteredData = (flashcardTrendData || []).filter((dp) => {
    const m = moment(dp.date);
    return m.month() === selectedMonth && m.year() === selectedYear;
  });

  const sortedData = filteredData ? [...filteredData] : [];
  sortedData.sort((a, b) => {
    return moment(a.date).valueOf() - moment(b.date).valueOf();
  });

  let labels: string[] = [];
  let scores: number[] = [];
  let pointRadius: number[] = [];

  if (sortedData.length === 1) {
    const singleDate = moment(sortedData[0].date).format('MMMM D');
    const label = singleDate;
    labels = ['', label, ''];
    scores = [
      sortedData[0].completedFlashcardCount,
      sortedData[0].completedFlashcardCount,
      sortedData[0].completedFlashcardCount,
    ];
    pointRadius = [0, 3, 0]; // Hide fake points, show actual point
  } else {
    labels = sortedData.map((dp) => {
      return moment(dp.date).format('MMMM D');
    });
    scores = sortedData.map((dp) => dp.completedFlashcardCount);
    pointRadius = new Array(scores.length).fill(3); // Show all points
  }

  const lineData = {
    labels: labels,
    datasets: [
      {
        label: 'Completed Flashcards',
        data: scores,
        borderColor: isDarkMode ? '#FEFEFE' : '#5a68af',
        borderWidth: 1,
        pointRadius: pointRadius,
        backgroundColor: 'transparent', // This will be overridden in useEffect
        fill: true,
        tension: 0.4,
        pointBackgroundColor: isDarkMode ? '#FEFEFE' : '#5a68af',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: 'rgba(255,255,255,.3)',
        },
        ticks: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'Poppins',
          },
        },
        border: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        grid: {
          display: false,
          color: 'rgba(255,255,255,.3)',
        },
        ticks: {
          stepSize: 10,
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'Poppins',
          },
        },
        border: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
        },
      },
    },
    animation: {
      onComplete: () => {
        const chart = chartRef.current;
        if (chart) {
          const ctx = chart.ctx;
          const gradient = ctx.createLinearGradient(
            0,
            0,
            0,
            chart.chartArea.bottom
          );
          gradient.addColorStop(
            0,
            isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(99, 133, 254, 0.5)'
          );
          gradient.addColorStop(
            1,
            isDarkMode ? 'rgba(255, 255, 255, .02)' : 'rgba(99, 133, 254, .02)'
          );
          chart.data.datasets[0].backgroundColor = gradient;
          chart.update();
        }
      },
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [flashcardTrendData]);

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Month selector options (sorted)
  const monthOptions = allMonths
    .map((ym) => {
      const m = moment(ym, 'YYYY-MM');
      return {
        value: ym,
        label: m.format('MMMM YYYY'),
        month: m.month(),
        year: m.year(),
      };
    })
    .sort((a, b) => {
      const dateA = moment(a.value, 'YYYY-MM');
      const dateB = moment(b.value, 'YYYY-MM');
      return dateB.valueOf() - dateA.valueOf(); // Descending order
    });

  // Handler for dropdown change
  const handleMonthChange = (event: SelectChangeEvent) => {
    const selected = monthOptions.find(
      (opt) => opt.value === event.target.value
    );
    if (selected) {
      setSelectedMonth(selected.month);
      setSelectedYear(selected.year);
    }
  };

  return (
    <Widget
      widgetKey={WIDGET_KEYS.FLASHCARD_TREND}
      name={'flashcard-trend'}
      title="Flashcard Trend"
      subtitle={primaryExam?.name + ' flashcard trend over time'}
      showDelete={showDelete}
    >
      <div className="qbank-performance">
        {flashcardTrendData && flashcardTrendData.length > 0 && (
          <div className="chart-container">
            <Line data={lineData} options={chartOptions} ref={chartRef} />
          </div>
        )}
        {flashcardTrendData && flashcardTrendData.length === 0 && (
          <div className="no-data">Flashcard trend data will appear here.</div>
        )}
        {flashcardTrendData && flashcardTrendData.length > 0 && (
          <div className="qbank-performance_footer">
            <div className="month-selector">
              <Select
                value={moment({
                  year: selectedYear,
                  month: selectedMonth,
                }).format('YYYY-MM')}
                onChange={handleMonthChange}
                aria-label="Select Month"
                style={monthOptions?.length > 1 ? {} : { display: 'none' }}
              >
                {monthOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        )}
      </div>
    </Widget>
  );
};

export default FlashcardTrend;
